import IconButton from "@mui/material/IconButton";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { MobileNavBarItem } from "@app.automotus.io/components/NavBar/NavBarItem/MobileNavBarItem";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import DrawerMenu from "@app.automotus.io/components/DrawerMenu";
import KeyboardArrowLeft from "@mui/icons-material/KeyboardArrowLeft";
import { GetAllInvoicesData } from "common/graphql/queries";
import { NavBarTarget } from "@app.automotus.io/components/NavBar";
import MenuIcon from "@mui/icons-material/Menu";
import AccountContextDropdown from "@app.automotus.io/components/AccountContextDropdown";

export const MobileNavBarMenu: React.FC<MobileNavBarMenuProps> = ({
  targets,
  invoicesData,
  invoicesLoading,
  supportLabel = "Support",
  supportUrl,
}) => {
  const [isDrawerOpen, setIsDrawerOpen] = useState<boolean>(false);
  const [isInvoicesShown, setIsInvoicesShown] = useState<boolean>(false);
  const navigate = useNavigate();

  const handleTargetClick = (targetProps: NavBarTarget) => (e: React.SyntheticEvent) => {
    if (targetProps.title === "Invoices") {
      setIsInvoicesShown(true);
      return;
    }
    if (targetProps.onClick) {
      targetProps.onClick(e);
    } else if (targetProps.linkType === "external") {
      // navigate() resolves `to` as an internal route relative to the current
      // location, which mangles an absolute URL (e.g. Support/Resources'
      // Zendesk links) into an invalid path instead of opening it.
      window.open(targetProps.to, "_blank", "noopener,noreferrer");
    } else {
      navigate(targetProps.to);
    }
  };

  const onCloseDrawer = () => {
    setIsDrawerOpen(false);
  };

  const onCloseSubMenuDrawer = () => {
    setIsInvoicesShown(false);
    onCloseDrawer();
  };

  const onOpenDrawer = () => {
    setIsDrawerOpen(true);
    setIsInvoicesShown(false);
  };

  useEffect(() => {
    isInvoicesShown && setIsDrawerOpen(true);
  }, [isInvoicesShown]);

  return (
    <>
      <IconButton color="inherit" aria-label="open drawer" edge="start" onClick={onOpenDrawer}>
        <MenuIcon />
      </IconButton>
      {!isInvoicesShown && (
        <DrawerMenu open={isDrawerOpen} onClose={onCloseDrawer} submenu={isInvoicesShown}>
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
            <AccountContextDropdown
              containerSx={{
                width: "100%",
                px: 2,
                py: 1.5,
                borderBottom: "1px solid rgba(255, 255, 255, 0.12)",
                "& .MuiSelect-select, & .MuiOutlinedInput-notchedOutline, & .MuiSvgIcon-root": {
                  color: "primary.white",
                  borderColor: "rgba(255, 255, 255, 0.23)",
                },
              }}
            />
            {targets
              .filter((targetProps) => targetProps.title !== "Logout")
              .map((targetProps) => (
                <MobileNavBarItem
                  key={targetProps.title}
                  title={targetProps.title}
                  icon={targetProps.hideIconOnMobile ? undefined : targetProps.icon}
                  onClick={handleTargetClick(targetProps)}
                />
              ))}
            {/* Skipped when a target already points at the same URL (e.g. CurbSuite's own
             * "Support" target) — otherwise that app's drawer would show the same link twice. */}
            {supportUrl && !targets.some((targetProps) => targetProps.to === supportUrl) && (
              <MobileNavBarItem
                key="Resources"
                title={supportLabel}
                onClick={() => window.open(supportUrl, "_blank", "noopener,noreferrer")}
              />
            )}
            {targets
              .filter((targetProps) => targetProps.title === "Logout")
              .map((targetProps) => (
                <MobileNavBarItem
                  key={targetProps.title}
                  title={targetProps.title}
                  icon={targetProps.hideIconOnMobile ? undefined : targetProps.icon}
                  onClick={handleTargetClick(targetProps)}
                />
              ))}
          </Box>
        </DrawerMenu>
      )}
      {isInvoicesShown && !invoicesLoading && (
        <DrawerMenu open={isInvoicesShown} onClose={onCloseSubMenuDrawer} submenu={isInvoicesShown}>
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                color: "rgba(0, 0, 0, 0.60)",
                py: 0.75,
                px: 2,
                width: "100%",
                borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
              }}
              onClick={() => setIsInvoicesShown(false)}
            >
              <KeyboardArrowLeft sx={{ width: 36, height: 36 }} />
              <Typography variant="subtitle2" sx={{ ml: 1 }}>
                All Invoices
              </Typography>
            </Box>
            {invoicesData?.invoices.map((invoice) => (
              <MobileNavBarItem
                key={invoice.index}
                title={String(invoice.index).padStart(6, "0")}
                icon={
                  invoice.amount_paid === 0 ? (
                    <Typography variant="caption" color="#E31B0C">
                      Unpaid
                    </Typography>
                  ) : null
                }
                onClick={() => {
                  navigate(`/invoices/${invoice.index}`);
                }}
                submenuItem={isInvoicesShown}
              />
            ))}
          </Box>
        </DrawerMenu>
      )}
    </>
  );
};

export interface MobileNavBarMenuProps {
  invoicesData?: GetAllInvoicesData;
  invoicesLoading?: boolean;
  targets: NavBarTarget[];
  supportLabel?: string;
  supportUrl?: string;
}
